import {
  DependencyList,
  RefObject,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

interface RectResult {
  bottom: number;
  height: number;
  left: number;
  right: number;
  top: number;
  width: number;
}

export const useRect = (
  ref: RefObject<HTMLElement | null>,
  deps: DependencyList = [],
): RectResult => {
  const [rect, setRect] = useState<RectResult>(getRect(ref?.current));
  const rafIdRef = useRef<number | null>(null);

  const handleResize = useCallback(() => {
    if (!ref.current) {
      return;
    }

    // Throttle updates using requestAnimationFrame to avoid excessive re-renders
    if (rafIdRef.current !== null) {
      return; // Already have a pending update
    }

    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null;
      if (ref.current) {
        setRect(getRect(ref.current));
      }
    });
  }, [ref, ...deps]);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    handleResize();

    if (typeof ResizeObserver === "function") {
      let resizeObserver: ResizeObserver | null = new ResizeObserver(() =>
        handleResize(),
      );
      resizeObserver.observe(element);

      return () => {
        if (!resizeObserver) {
          return;
        }

        resizeObserver.disconnect();
        resizeObserver = null;

        // Clean up any pending RAF
        if (rafIdRef.current !== null) {
          cancelAnimationFrame(rafIdRef.current);
          rafIdRef.current = null;
        }
      };
    }

    return undefined;
  }, [ref.current, ...deps]);

  return rect;
};

function getRect(element: HTMLElement | null): RectResult {
  if (!element) {
    return {
      bottom: 0,
      height: 0,
      left: 0,
      right: 0,
      top: 0,
      width: 0,
    };
  }

  return element.getBoundingClientRect();
}
